import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  EllipsisVIcon,
  FileIcon,
  GreyIconWrapper,
  SingleInfoCard,
  Theme,
  Tooltip,
  Typography,
  makeStyles,
  useModal,
} from '@forgeglobal/design-framework'
import { FileFragment, useGetAllDocumentTagsQuery, useGetDocumentsByAccountQuery } from 'graphql/bindings/AdminBindings'
import { PopoverAction } from 'isolated-design-library'
import { useDropzone } from 'react-dropzone'
import React, { Fragment, useState } from 'react'
import moment from 'moment'

import { UploadDocumentsModal } from './UploadDocumentsModal'
import DocumentActions from './DocumentActions'
import DocumentList, { AccountFile } from './DocumentList'

interface FileWithMetaData extends File {
  path?: string
  documentType?: string
  tag?: string
}

export const tagMap: { [key: string]: string } = {
  'broker-referral-agreement': 'Referral',
  'broker-transaction-agreement': 'Transaction',
  'market-console-general-doc': 'General',
}

const Documents: React.FC<{ accountId: string }> = ({ accountId }) => {
  const { data } = useGetDocumentsByAccountQuery({
    variables: { accountId },
  })
  const files = data?.account.files ?? []
  const parents = data?.account.parents.map(parent => parent.parent_account) ?? []
  const introducers = data?.account.introducer_accounts ?? []
  const relatedAccounts = [...parents, ...introducers]

  const relatedFiles: AccountFile[] = relatedAccounts.flatMap(({ files, id, name }) => {
    return files.map(file => {
      return {
        file,
        account: {
          id,
          name,
        },
      }
    })
  })

  const [fileData, setFileData] = useState<File[]>([])
  const [isDropping, setIsDropping] = useState(false)

  const tagData = useGetAllDocumentTagsQuery()
  const tags = tagData?.data?.allTags ?? []

  const filteredTags = tags
    .filter(item => tagMap[item.tag])
    .map(item => {
      return { label: tagMap[item.tag], value: item.id }
    })

  const useStyles = makeStyles((theme: Theme) => ({
    dropping: {
      border: `5px dashed ${theme.palette.divider}`,
    },
  }))

  const removeDocument = (name: string) => {
    const newFiles = fileData.filter(file => file.name !== name)
    setFileData(newFiles)
  }

  const [showUploadDocumentsModal, hideUploadDocumentsModal] = useModal(() => {
    return (
      <UploadDocumentsModal
        hideModal={hideUploadDocumentsModal}
        files={fileData}
        pageSource="account"
        accountId={accountId}
        tags={filteredTags}
        removeDocument={removeDocument}
      />
    )
  }, [fileData])

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: '.pdf, .doc, .docx',
    onDropRejected: (rejectedFiles: FileWithMetaData[]) => {
      rejectedFiles.forEach(file => {
        alert(`${file.path} is not a valid document. We only currently support .doc, .docx, and .pdf files.`)
      })
    },
    onDrop: acceptedFiles => {
      setFileData(acceptedFiles)
      showUploadDocumentsModal()
      setIsDropping(false)
    },
    onDragEnter: () => {
      setIsDropping(true)
    },
    onDragLeave: () => {
      setIsDropping(false)
    },
  })

  const classes = useStyles()

  return (
    <Card>
      <CardHeader title="Documents" />
      <div className={isDropping ? classes.dropping : ''} {...(getRootProps() as any)}>
        <input {...(getInputProps() as any)} />
        <CardContent>
          <Box mb={2}>
            <Typography variant="caption" color="textPrimary">
              Supported file formats include .doc, .docx, and .pdf
            </Typography>
          </Box>

          {files.map((file: FileFragment) => {
            const { filename: name, date_created } = file
            const nameComponents = name ? name.split('.') : []
            const fileType = nameComponents[nameComponents.length - 1]
            const title = name && name.length > 48 ? `${name?.substring(0, 22)}...${fileType}` : name
            return (
              <SingleInfoCard
                openInNewTab
                key={date_created.toString()}
                logo={
                  <GreyIconWrapper>
                    <FileIcon />
                  </GreyIconWrapper>
                }
                leftTitle={
                  (
                    <Tooltip title={name ?? ''} placement="top">
                      <div style={{ maxWidth: 300 }}>{title}</div>
                    </Tooltip>
                  ) ?? 'ERROR: Name not found'
                }
                leftSubtitle={`Uploaded ${moment(date_created).format('MM/DD/YYYY')} | ${tagMap[file.tags[0]?.tag] ??
                  'Document tag not found'}`}
                action={
                  <PopoverAction
                    icon={<EllipsisVIcon />}
                    actionMenu={<DocumentActions file={file} accountId={accountId} type="primary" />}
                  />
                }
              />
            )
          })}

          <Box mt={2}>
            <Button size="large" fullWidth onClick={open}>
              Upload Documents
            </Button>
          </Box>

          {relatedFiles.length !== 0 && (
            <Fragment>
              <Box mb={1} mt={1}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Related Documents
                </Typography>
              </Box>
              <DocumentList files={relatedFiles} accountId={accountId} type={'related'} />
            </Fragment>
          )}
        </CardContent>
      </div>
    </Card>
  )
}

export default Documents
